import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationCenter = ({ inSidebar = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'critical',
      title: 'Production Line 3 Alert',
      message: 'Temperature threshold exceeded on Station 4',
      timestamp: new Date(Date.now() - 5 * 60000),
      read: false,
      priority: 'high'
    },
    {
      id: 2,
      type: 'warning',
      title: 'Quality Check Required',
      message: 'Batch #2847 requires inspection before proceeding',
      timestamp: new Date(Date.now() - 15 * 60000),
      read: false,
      priority: 'medium'
    },
    {
      id: 3,
      type: 'info',
      title: 'Shift Change Reminder',
      message: 'Day shift ends in 30 minutes',
      timestamp: new Date(Date.now() - 30 * 60000),
      read: true,
      priority: 'low'
    },
    {
      id: 4,
      type: 'success',
      title: 'Production Target Met',
      message: 'Line 2 achieved 105% of daily target',
      timestamp: new Date(Date.now() - 60 * 60000),
      read: true,
      priority: 'low'
    }
  ]);

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const toggleNotificationCenter = () => {
    setIsOpen(!isOpen);
  };

  const markAsRead = (id) => {
    setNotifications(notifications?.map(n =>
      n?.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications?.map(n => ({ ...n, read: true })));
  };

  const clearNotification = (id) => {
    setNotifications(notifications?.filter(n => n?.id !== id));
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'critical':
        return 'AlertOctagon';
      case 'warning':
        return 'AlertTriangle';
      case 'success':
        return 'CheckCircle2';
      case 'info':
      default:
        return 'Info';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'critical':
        return 'var(--color-error)';
      case 'warning':
        return 'var(--color-warning)';
      case 'success':
        return 'var(--color-success)';
      case 'info':
      default:
        return 'var(--color-primary)';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = Math.floor((now - timestamp) / 60000);

    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  return (
    <div className={inSidebar ? 'relative' : 'fixed left-4 top-4 z-[100]'}>
      <button
        onClick={toggleNotificationCenter}
        className="relative flex items-center justify-center w-8 h-8 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={14} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-[10px] font-semibold text-white bg-error rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[190]"
            onClick={toggleNotificationCenter}
            aria-hidden="true"
          />
          <div className={`absolute ${inSidebar ? 'left-0 top-12' : 'right-0 top-12'} w-96 max-w-[calc(100vw-2rem)] bg-card rounded-xl shadow-xl border border-border z-[200] max-h-[600px] flex flex-col`}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                >
                  Mark all read
                </Button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {notifications?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-6">
                  <Icon name="Bell" size={48} color="var(--color-muted-foreground)" />
                  <p className="mt-4 text-muted-foreground">No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {notifications?.map((notification) => (
                    <div
                      key={notification?.id}
                      className={`px-6 py-4 hover:bg-muted/50 transition-colors duration-250 ${!notification?.read ? 'bg-muted/30' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <Icon
                            name={getNotificationIcon(notification?.type)}
                            size={20}
                            color={getNotificationColor(notification?.type)}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="text-sm font-medium text-foreground">
                              {notification?.title}
                            </h4>
                            <button
                              onClick={() => clearNotification(notification?.id)}
                              className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors duration-250"
                              aria-label="Clear notification"
                            >
                              <Icon name="X" size={16} />
                            </button>
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {notification?.message}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="caption text-muted-foreground">
                              {formatTimestamp(notification?.timestamp)}
                            </span>
                            {!notification?.read && (
                              <button
                                onClick={() => markAsRead(notification?.id)}
                                className="caption text-primary hover:text-primary/80 transition-colors duration-250"
                              >
                                Mark as read
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-border">
              <Button
                variant="outline"
                fullWidth
                onClick={() => console.log('View all notifications')}
              >
                View All Notifications
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;